import React, { useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { useIntersectionObserver } from "../hooks/useIntersectionObserver";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ProjectsSection = styled.section`
  padding: 3rem 2rem;
  background: ${({ theme }) => theme.surface};
  border-radius: 12px;
  margin: 2rem auto;
  max-width: 1200px;
  box-shadow: ${({ theme }) => theme.card.shadow};
  opacity: 1;
  ${({ isVisible }) =>
    isVisible &&
    css`
      animation: ${fadeInUp} 0.6s ease-out forwards;
    `}

  @media (max-width: 768px) {
    padding: 2rem 1rem;
    margin: 1rem;
  }
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.text.primary};
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ProjectCardWrapper = styled.article`
  background: ${({ theme }) => theme.card.background};
  border-radius: 12px;
  overflow: hidden;
  opacity: 0;
  position: relative;
  height: 100%;
  min-height: 480px;
  box-shadow: ${({ theme }) => theme.card.shadow};
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  ${({ isVisible, index }) =>
    isVisible &&
    css`
      animation: ${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
      animation-delay: ${index * 0.2}s;
    `}
`;

const CardInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.8s;
  transform-style: preserve-3d;
  transform: ${(props) => (props.isFlipped ? "rotateY(180deg)" : "rotateY(0)")};
`;

const CardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 12px;
  background: ${({ theme }) => theme.card.background};
  display: flex;
  flex-direction: column;
`;

const CardBack = styled(CardFace)`
  transform: rotateY(180deg);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;

  ul {
    margin: 0;
    padding-left: 1.2rem;
    font-size: 0.9rem;
  }

  h4 {
    margin: 0.75rem 0 0.5rem;
    font-size: 1rem;
  }
`;

const FlipButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${({ theme }) => theme.card.background};
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.text.primary};
  transition: all 0.3s ease;
  z-index: 2;
  box-shadow: ${({ theme }) => theme.card.shadow};

  &:hover {
    transform: rotate(180deg);
    background: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.button.text};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  padding-top: 50%;
  border-radius: 12px 12px 0 0;
`;

const ProjectImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ProjectTitle = styled.h3`
  color: ${({ theme }) => theme.text.primary};
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const ProjectDescription = styled.p`
  color: ${({ theme }) => theme.text.secondary};
  line-height: 1.4;
  margin: 0.5rem 0;
  font-size: 0.9rem;
  flex: 1;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: 1rem;
  position: relative;
  bottom: 0;
`;

const ProjectButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.2rem;
  background: ${({ theme, variant }) =>
    variant === "primary" ? theme.primary : "transparent"};
  color: ${({ theme, variant }) =>
    variant === "primary" ? theme.button.text : theme.text.primary};
  border: 2px solid ${({ theme }) => theme.primary};
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  min-width: 120px;
  text-align: center;

  &:hover {
    transform: translateY(-2px);
    background: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.button.text};
    box-shadow: 0 4px 8px ${({ theme }) => `${theme.primary}30`};
  }

  &:active {
    transform: translateY(0);
  }
`;

const TechList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 1rem 0;
`;

const TechTag = styled.span`
  background: ${({ theme }) => `${theme.primary}15`};
  color: ${({ theme }) => theme.primary};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.button.text};
    transform: translateY(-2px);
  }
`;

const projects = [
  {
    title: "Nasflix",
    description:
      "A video streaming web app inspired by Netflix. Built using JavaScript, Ruby on Rails, PostgreSQL, AWS S3, React, and CSS.",
    image: "/images/thumbs/nasflix.png",
    liveLink: "https://nasflix.herokuapp.com/#/",
    githubLink: "https://github.com/sanaanymoe/NasFlix",
    techStack: [
      "React",
      "Ruby on Rails",
      "PostgreSQL",
      "AWS S3",
      "JavaScript",
      "CSS",
    ],
    features: [
      "User authentication",
      "Video streaming",
      "Custom video player",
      "Search functionality",
    ],
  },
  {
    title: "Wassup",
    description:
      "Chat web application inspired by WhatsApp. Built using HTML, CSS, React Hooks, and Google Firebase.",
    image: "/images/thumbs/wassup3.png",
    liveLink: "https://wassup-ed4d5.web.app/",
    githubLink: "https://github.com/sanaanymoe/wassup",
    techStack: ["React", "Firebase", "HTML", "CSS"],
    features: [
      "Real-time messaging",
      "User authentication",
      "Responsive design",
    ],
  },
  {
    title: "BeatPop",
    description:
      "A web application dynamically responds to changes in music volume and frequency spectrum to generate animated shapes. Built using JavaScript, CSS, and HTML canvas.",
    image: "/images/thumbs/beatpop.png",
    liveLink: "https://sanaanymoe.github.io/BeatPop/",
    githubLink: "https://github.com/sanaanymoe/BeatPop",
    techStack: ["JavaScript", "HTML Canvas", "CSS"],
    features: [
      "Audio visualization",
      "Interactive animations",
      "Customizable settings",
    ],
  },
  {
    title: "MyTinerary",
    description:
      "A web application designed to plan and customize trips. Built using JavaScript, React with Redux, MongoDB, Node.js, Express.js, and various APIs for flights, hotels, and events data.",
    image: "/images/thumbs/MyTinerary.png",
    liveLink: "https://github.com/sanaanymoe/MyTinerary",
    githubLink: "https://github.com/sanaanymoe/MyTinerary",
    techStack: ["React", "Redux", "MongoDB", "Node.js", "Express.js", "APIs"],
    features: ["Trip planning", "Custom itineraries", "Integration with APIs"],
  },
];

const ProjectCard = ({ project, index, isVisible }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const toggleFlip = () => setIsFlipped(!isFlipped);

  return (
    <ProjectCardWrapper index={index} isVisible={isVisible}>
      <FlipButton onClick={toggleFlip} aria-label="Toggle project details">
        <i className="fas fa-sync-alt" />
      </FlipButton>
      <CardInner isFlipped={isFlipped}>
        <CardFace>
          <ImageContainer>
            <ProjectImage src={project.image} alt={project.title} />
          </ImageContainer>
          <ProjectContent>
            <ProjectTitle>{project.title}</ProjectTitle>
            <ProjectDescription>{project.description}</ProjectDescription>
            <ButtonContainer>
              <ProjectButton
                href={project.liveLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="primary"
              >
                Live Demo
              </ProjectButton>
              <ProjectButton
                href={project.githubLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                GitHub
              </ProjectButton>
            </ButtonContainer>
          </ProjectContent>
        </CardFace>
        <CardBack>
          <ProjectTitle>{project.title}</ProjectTitle>
          <h4
            style={{
              color: (theme) => theme.text.secondary,
              marginBottom: "1rem",
            }}
          >
            Tech Stack:
          </h4>
          <TechList>
            {project.techStack.map((tech, i) => (
              <TechTag key={i}>{tech}</TechTag>
            ))}
          </TechList>
          <h4
            style={{
              color: (theme) => theme.text.secondary,
              margin: "1.5rem 0 1rem",
            }}
          >
            Key Features:
          </h4>
          <ul
            style={{
              color: (theme) => theme.text.secondary,
              paddingLeft: "1.2rem",
              lineHeight: "1.6",
            }}
          >
            {project.features.map((feature, i) => (
              <li key={i}>{feature}</li>
            ))}
          </ul>
        </CardBack>
      </CardInner>
    </ProjectCardWrapper>
  );
};

const Projects = () => {
  const [sectionRef, isSectionVisible] = useIntersectionObserver({
    threshold: 0.1,
  });

  return (
    <ProjectsSection ref={sectionRef} isVisible={isSectionVisible}>
      <SectionTitle>Recent Projects</SectionTitle>
      <ProjectGrid>
        {projects.map((project, index) => (
          <ProjectCard
            key={project.title}
            project={project}
            index={index}
            isVisible={isSectionVisible}
          />
        ))}
      </ProjectGrid>
    </ProjectsSection>
  );
};

export default Projects;
